import { useState } from "react";
import type { JobNotificationDto } from "@/lib/job-notifications";

export interface NotificationBellProps {
  notifications: JobNotificationDto[];
  onMarkRead: (id: number) => void;
}

export function NotificationBell({ notifications, onMarkRead }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const count = notifications.length;

  return (
    <div className="relative inline-block" data-testid="notification-bell">
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        className="relative rounded-full p-2 hover:bg-secondary"
        onClick={() => setOpen((v) => !v)}
      >
        {/* Bell icon (simplified SVG) */}
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {count > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
            data-testid="notification-badge"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-1 w-72 rounded-lg border border-border bg-popover shadow-md"
          data-testid="notification-list"
        >
          {notifications.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No notifications</p>
          ) : (
            <ul className="divide-y">
              {notifications.map((n) => (
                <li key={n.jobNotificationId} className="flex items-start justify-between gap-2 p-3">
                  <div className="min-w-0 space-y-0.5">
                    <p className="truncate text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground">{n.jobReferenceNo}</p>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-primary hover:underline"
                    onClick={() => onMarkRead(n.jobNotificationId)}
                  >
                    Mark read
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
